import {Button, Component, KeyEvent, Layout, SlideoutDecorator} from "@mcleod/components";
import {LogManager} from "@mcleod/core";
import {AutogenLayoutEditEquipment} from "../asset/autogen/AutogenLayoutEditEquipment";
import { PlanningUtil } from "./PlanningUtil";
import { getDispatchControlValue } from "@mcleod/general";

interface EditEquipSlideoutProps {
    driverName: string;
    driverId: string;
    driver2Name?: string;
    driver2Id?: string;
    trailerId: string;
    trailer2Id?: string;
    trailer3Id?: string;
    tractorId: string;
    orderId: string;
    hideTeamDriver?: boolean;
}

const actionButtonProps = {
    caption: "Save",
    backgroundColor: "primary",
    color: "primary.reverse",
    busyWhenDataSourceBusy: true,
    paddingTop: 10,
    paddingBottom: 10,
    rowBreak: false,
    borderWidth: 0,
    minWidth: 128
}

const log = LogManager.getLogger("lme.dispatch.asset.planning");

export class EditEquipment extends AutogenLayoutEditEquipment {

    public static showSlideout(props: EditEquipSlideoutProps) {
        const dcMaxTrailers = getDispatchControlValue("maximum_trailers");
        const layout = Layout.getLayout("lme/dispatch/asset/EditEquipment") as EditEquipment;
        const actionButton = new Button({...actionButtonProps});
        new SlideoutDecorator({
            layout,
            fillHeight: true,
            width: 1000,
            minWidth: 1000,
            title: "Edit Planning Comments",
            displayOverlay: true,
            addlComponents: [actionButton],
            overlayProps: {greyedBackground: true},
            layoutLoadListeners: () => {
                layout.addKeyDownListener((event: KeyEvent) => PlanningUtil.savePlanningCommentOnKeyDown(event, actionButton));
                const {driverName, driverId, driver2Name, driver2Id, trailerId, trailer2Id, trailer3Id, tractorId, orderId} = props;
                if (trailerId == null) {
                    this.setupForNoEquipment(layout.textboxTrailerPlanningComment, `There is no trailer ${dcMaxTrailers > 1 ? "1 " : ""}assigned to this move`);
                } else {
                    layout.textboxTrailerPlanningComment.caption = `Trailer ${dcMaxTrailers > 1 ? "1 " : ""}(${trailerId}) Planning Comment`;
                }

                if (dcMaxTrailers >= 2) {
                    // Trailer 2
                    layout.textboxTrailer2PlanningComment.visible = true;
                    if (trailer2Id == null) {
                        this.setupForNoEquipment(layout.textboxTrailer2PlanningComment, "There is no trailer 2 assigned to this move");
                    } else {
                        layout.textboxTrailer2PlanningComment.caption = `Trailer 2 (${trailer2Id}) Planning Comment`;
                    }
                    if (dcMaxTrailers >= 3) {
                        // Trailer 3
                        layout.textboxTrailer3PlanningComment.visible = true;
                        if (trailer3Id == null) {
                            this.setupForNoEquipment(layout.textboxTrailer3PlanningComment, "There is no trailer 3 assigned to this move");
                        } else {
                            layout.textboxTrailer3PlanningComment.caption = `Trailer 3 (${trailer3Id}) Planning Comment`;
                        }
                    }
                }

                if (tractorId == null) {
                    this.setupForNoEquipment(layout.textboxTractorPlanningComment, "There is no tractor assigned to this move.");
                } else {
                    layout.textboxTractorPlanningComment.caption = `Tractor (${tractorId}) Planning Comment`;
                }

                if (driverId == null) {
                    this.setupForNoEquipment(layout.textboxDriverPlanningComment, "There is no driver 1 assigned to this move.");
                } else {
                    layout.textboxDriverPlanningComment.caption = `Driver ${props.hideTeamDriver ? "" : "1 "}(${driverName ?? driverId}) Planning Comment`;
                }

                layout.textboxDriver2PlanningComment.visible = !props.hideTeamDriver;
                if (driver2Id == null) {
                    this.setupForNoEquipment(layout.textboxDriver2PlanningComment, "There is no driver 2 assigned to this move.");
                } else {
                    layout.textboxDriver2PlanningComment.caption = `Driver 2 (${driver2Name ?? driver2Id}) Planning Comment`;
                }

                if (orderId == null) {
                    this.setupForNoEquipment(layout.textboxOrderPlanningComment, "There is no order assigned to this move.");
                } else {
                    layout.textboxOrderPlanningComment.caption = `Order (${orderId}) Planning Comment`
                }

                actionButton.addClickListener(async () => {
                    actionButton.busy = true;
                    const postPromises = [];
                    if (layout.sourceTrailer.hasChanged()) postPromises.push(layout.sourceTrailer.post());
                    if (layout.sourceTrailer2.hasChanged()) postPromises.push(layout.sourceTrailer2.post());
                    if (layout.sourceTrailer3.hasChanged()) postPromises.push(layout.sourceTrailer3.post());
                    if (layout.sourceDriver.hasChanged()) postPromises.push(layout.sourceDriver.post());
                    if (layout.sourceDriver2.hasChanged()) postPromises.push(layout.sourceDriver2.post());
                    if (layout.sourceTractor.hasChanged()) postPromises.push(layout.sourceTractor.post());
                    if (layout.sourceOrders.hasChanged()) postPromises.push(layout.sourceOrders.post());
                    await Promise.all(postPromises)
                        .catch((error) => {
                            log.error("Error in saving comments.", error)
                        })
                        .finally(() => {
                            actionButton.busy = false;
                            layout.slideOut();
                        });
                });

                if (driverId != null) layout.sourceDriver.search({id: driverId});
                if (driver2Id != null) layout.sourceDriver2.search({id: driver2Id});
                if (tractorId != null) layout.sourceTractor.search({id: tractorId});
                if (trailerId != null) layout.sourceTrailer.search({id: trailerId});
                if (trailer2Id != null) layout.sourceTrailer2.search({id: trailer2Id});
                if (trailer3Id != null) layout.sourceTrailer3.search({id: trailer3Id});
                if (orderId != null) layout.sourceOrders.search({id: orderId});
            },
        })
    }

    private static setupForNoEquipment(comp: Component, tooltip: string) {
        comp.tooltip = tooltip;
        comp.enabled = false;
    }

}
